import { History } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import type { StayPlanAdjustment } from "@/hooks/useStayPlanAdjustments";
import type { StayPlanContract } from "@/hooks/useStayPlanContracts";
import { formatDateBR } from "@/lib/date-utils";

export interface StayPlanAdjustmentHistoryProps {
  contract: StayPlanContract;
  onReverse: (adjustment: StayPlanAdjustment) => void;
}

function formatCurrency(value: number, language: string) {
  return new Intl.NumberFormat(language.startsWith("pt") ? "pt-BR" : "en-US", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function StayPlanAdjustmentHistory({ contract, onReverse }: StayPlanAdjustmentHistoryProps) {
  const { t, i18n } = useTranslation("patients");
  const adjustments = contract.adjustments;
  // Um ajuste que já tem reversão apontando para ele não pode ser revertido de
  // novo — o índice único no banco garante isso; aqui só refletimos na UI.
  const reversedIds = new Set(adjustments.map((item) => item.reversesAdjustmentId).filter(Boolean) as string[]);

  return (
    <div className="mt-[18px]">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <History className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <p className="font-display text-sm font-bold text-foreground">{t("stayPlanAdjustments.historyTitle")}</p>
        {adjustments.length ? (
          <p className="text-xs text-color-light">
            {t("stayPlanAdjustments.historyCount", { count: adjustments.length })}
          </p>
        ) : null}
      </div>

      {adjustments.length === 0 ? (
        <p className="mt-2.5 rounded-lg border border-border bg-cream-50 px-4 py-4 text-sm text-muted-foreground">
          {t("stayPlanAdjustments.historyEmpty")}
        </p>
      ) : (
        <ul className="mt-2.5 overflow-hidden rounded-lg border border-border">
          {adjustments.map((adjustment) => {
            const isReversal = adjustment.reversesAdjustmentId !== null;
            const wasReversed = reversedIds.has(adjustment.id);
            return (
              <li
                key={adjustment.id}
                className="flex flex-col items-center gap-2 border-b border-border bg-background px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-3.5"
              >
                <span
                  className={`inline-flex w-fit shrink-0 items-center justify-center rounded-md border px-2.5 py-1 text-sm font-bold ${
                    adjustment.delta > 0
                      ? "border-state-success/30 bg-state-success-soft text-state-success-foreground"
                      : "border-state-danger/30 bg-state-danger-soft text-state-danger-foreground"
                  }`}
                >
                  {adjustment.delta > 0 ? `+${adjustment.delta}` : `−${Math.abs(adjustment.delta)}`}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-[0.8125rem] font-semibold text-foreground">
                    {t(`stayPlanAdjustments.reasons.${adjustment.reason}`)}
                  </p>
                  {adjustment.note ? <p className="text-xs text-muted-foreground">{adjustment.note}</p> : null}
                  {adjustment.targetWeekStartsOn ? (
                    <p className="text-xs text-muted-foreground">
                      {t("stayPlanAdjustments.targetWeek", { date: formatDateBR(adjustment.targetWeekStartsOn) })}
                    </p>
                  ) : null}
                  {adjustment.cancelledStayReservationDayId ? (
                    <p className="text-xs text-muted-foreground">{t("stayPlanAdjustments.cancelledDayForRemoval")}</p>
                  ) : null}
                  {isReversal ? (
                    <p className="text-xs text-muted-foreground">{t("stayPlanAdjustments.reversesOther")}</p>
                  ) : null}
                </div>

                <span
                  className={`w-fit shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    adjustment.chargeAmount
                      ? "bg-state-warning-soft text-state-warning-foreground"
                      : "bg-state-success-soft text-state-success-foreground"
                  }`}
                >
                  {adjustment.chargeAmount
                    ? t("stayPlanAdjustments.chargedAmount", {
                        amount: formatCurrency(adjustment.chargeAmount, i18n.language),
                      })
                    : t("stayPlanAdjustments.noCharge")}
                </span>

                <div className="shrink-0 text-xs text-muted-foreground sm:text-right">
                  <p className="font-semibold text-color-medium">{adjustment.createdByName}</p>
                  <p className="mt-0.5 text-color-light">
                    {new Date(adjustment.createdAt).toLocaleString(
                      i18n.language.startsWith("pt") ? "pt-BR" : "en-US",
                      { dateStyle: "short", timeStyle: "short" },
                    )}
                  </p>
                </div>

                {wasReversed ? (
                  <span className="shrink-0 text-xs text-color-light">{t("stayPlanAdjustments.reversed")}</span>
                ) : isReversal ? null : (
                  <Button type="button" size="sm" variant="ghost" className="shrink-0" onClick={() => onReverse(adjustment)}>
                    {t("stayPlanAdjustments.reverse")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
